/**
 * Self-contained i18n for the Export Control feature.
 *
 * The export-control screens were ported from a standalone Vite app and never
 * wired into the language switcher, so they stayed English-only. This module
 * fixes that WITHOUT touching anything outside the feature folder: it reuses
 * the app-wide language switcher via the shared `useTranslation` hook (the same
 * one the tariff-classifier feature imports from `store/languageStore`) purely
 * to read the current language, then resolves keys against a local EN/DE
 * dictionary kept here in the feature.
 *
 * Because `useTranslation` subscribes to the language store, every
 * export-control component re-renders when the user flips the switcher, so the
 * UI updates live.
 */
import { useTranslation as useAppTranslation } from '../../../store/languageStore';

type Lang = 'en' | 'de';

type Dict = Record<string, string>;

const en: Dict = {
  // ── Wrapper / shell ───────────────────────────────────────────────
  'wrapper.title': 'AI Export Control',
  'wrapper.subtitle': 'Screen products for EU dual-use controls, licence requirements, and sanctions before you export.',
  'wrapper.tab.license': 'License Check',
  'wrapper.tab.extract': 'Dual-Use Check',
  'wrapper.tab.history': 'Audit History',
  'wrapper.how.title': 'How it works:',
  'wrapper.how.license': '• License Check: Determine whether an export licence is required for given AL numbers and destination.',
  'wrapper.how.dualUse': '• Dual-Use Check: Screen a product against EU dual-use control lists.',
  'wrapper.how.extract': '– Extract Components: Use when you only have a product name. The system identifies likely internal components from the product name, generates technical descriptions for each component, and classifies them individually against the control list.',
  'wrapper.how.compliance': '– Run Compliance Check: Use when you have finished-product descriptions. The product as a whole is classified against the control list — faster, and the right choice when you don\'t have a component-level breakdown.',
  'wrapper.how.history': '• Audit History: Review past sessions with their inputs, dual-use results, license verdicts, and risk assessments.',

  // ── Shared ────────────────────────────────────────────────────────
  'common.error': 'Error:',
  'common.errorOccurred': 'An error occurred',
  'common.yes': 'YES',
  'common.no': 'NO',
  'common.remove': 'Remove',
  'common.removeProductTitle': 'Remove this product',
  'common.isoHelp': 'Enter 2-letter ISO country code (e.g., US, DE, RU, CN, GB, FR)',
  'errors.invalidIso': 'Enter a valid ISO code',

  // ── License conditions (shared by License & Extract) ──────────────
  'cond.legalConditions': 'Legal Conditions',
  'cond.legalConditionsOptional': '⚖️ Legal Conditions (Optional)',
  'cond.transactionType': 'Transaction Type',
  'cond.repair': 'Repair/Replacement',
  'cond.repairHint': 'Items reimported for maintenance within 5 years (EU003)',
  'cond.exhibition': 'Temporary Export for Exhibition/Fair',
  'cond.exhibitionHint': 'Items will be reimported after event (EU004)',
  'cond.reimport120': 'Reimport within 120 days',
  'cond.reimport120Hint': 'Required for EU004 eligibility',
  'cond.intraGroup': 'Intra-group Transfer',
  'cond.intraGroupHint': 'Export to subsidiary/sister company (EU007)',
  'cond.destinationConcerns': 'Destination Concerns',
  'cond.freeZone': 'Customs-Free Zone / Free Warehouse',
  'cond.freeZoneHint': 'Export to duty-free zone (blocks all GEAs)',
  'cond.endUserConcerns': 'End-User / End-Use Concerns',
  'cond.informedAuthority': 'Informed by Competent Authority',
  'cond.informedAuthorityHint': 'Authority has notified of potential WMD/military end-use',
  'cond.wmd': 'Aware of WMD End-Use',
  'cond.wmdHint': 'Exporter aware items may be used for weapons of mass destruction',
  'cond.military': 'Military/Security End-User',
  'cond.militaryHint': 'Military, paramilitary, police, or intelligence entity recipient',
  'cond.humanRights': 'Human Rights Concern',
  'cond.humanRightsHint': 'Potential use for surveillance/human rights violations',

  // ── LicenseCheck ──────────────────────────────────────────────────
  'license.title': 'EU Export Control – License Determination',
  'license.countryInfo': 'Country Information',
  'license.destinationLabel': 'Destination Country (ISO Alpha-2):',
  'license.destinationPlaceholder': 'e.g. US, CN, IR',
  'license.originLabel': 'Country of Origin (ISO Alpha-2):',
  'license.originPlaceholder': 'e.g. DE, US, JP',
  'license.shippingLabel': 'Shipping Country (ISO Alpha-2):',
  'license.shippingPlaceholder': 'e.g. DE, NL, BE',
  'license.productInfo': 'Product Information',
  'license.alLabel': 'AL Numbers (comma-separated):',
  'license.alPlaceholder': 'e.g. 5A002.a.1, 5D002, 3A001.a.3',
  'license.contextLabel': 'Additional Context (Optional):',
  'license.contextPlaceholder': 'e.g. Commercial telecommunications equipment for retail sale...',
  'license.analyzing': 'Analyzing...',
  'license.determine': 'Determine License',
  'license.analysisTitle': 'License Determination Analysis',
  'license.determination': 'License Determination',
  'license.codeDriven': 'Code-Driven',
  'license.licenseRequired': 'License Required:',
  'license.authorisation': 'Authorisation:',
  'license.legalJustification': 'Legal Justification:',
  'license.legalReference': '📜 Legal Reference & Explanation',
  'license.riskAssessment': '🛡️ Transaction Risk Assessment',
  'license.resultTitle': 'License Determination Result',
  'license.individualRequired': 'Individual License Required:',
  'license.reason': 'Reason:',
  'license.errBoth': 'Please enter both AL Numbers and Destination',
  'license.errInvalidAl': 'Invalid AL number format: {values}. Expected: Category(0-9) + Letter(A-E) + 3 digits, with optional dotted sub-items (e.g. 5A002, 3A001.a.3, 1C350).',
  'license.errFailed': 'Failed to check license',

  // ── ExtractComponents ─────────────────────────────────────────────
  'extract.title': 'AI Export Control – Dual-Use Screening',
  'extract.countryInfo': 'Country Information',
  'extract.destinationLabel': 'Destination Country (ISO):',
  'extract.originLabel': 'Country of Origin (ISO):',
  'extract.shippingLabel': 'Shipping Country (ISO):',
  'extract.countryPlaceholder': 'e.g. US, CN, DE',
  'extract.exportContextLabel': 'Export Context (Optional):',
  'extract.exportContextPlaceholder': 'e.g. Commercial telecommunications equipment for retail sale, Repair and return, Exhibition...',
  'extract.productDescriptions': 'Product Descriptions',
  'extract.product': 'Product',
  'extract.productPlaceholder': 'Enter product description',
  'extract.addProduct': '+ Add product',
  'extract.fillExistingFirst': 'Fill in the existing product description first',
  'extract.clearToAddMore': 'Clear the form to add more products',
  'extract.extracting': 'Extracting...',
  'extract.extractComponents': 'Extract Components',
  'extract.checking': 'Checking...',
  'extract.runCompliance': 'Run Compliance Check',
  'extract.clear': 'Clear',
  'extract.orUpload': 'Or Upload PDF / Image',
  'extract.uploadHint': 'Upload a technical document — components will be extracted automatically and populated below.',
  'extract.chooseFile': 'Choose PDF or Image File',
  'extract.uploading': 'Uploading...',
  'extract.uploadExtract': 'Upload & Extract',
  'extract.extractedProducts': 'Extracted Products',
  'extract.extractedProductsHint': 'We extracted these products from your file and composed a description for each. Review and edit if needed — the compliance check will use the description below as-is.',
  'extract.productNamePlaceholder': 'Product name',
  'extract.composedDescPlaceholder': 'Composed product description (used for dual-use check)',
  'extract.runComplianceBolt': '⚡ Run Compliance Check',
  'extract.selectComponents': 'Select/Add Components for Dual-Use Check',
  'extract.componentNamePlaceholder': 'Component Name',
  'extract.modified': 'Modified',
  'extract.techDescPlaceholder': 'Technical description',
  'extract.category': 'Category:',
  'extract.cat.component': 'Component',
  'extract.cat.electronics': 'Electronics',
  'extract.cat.sensor': 'Sensor',
  'extract.cat.subsystem': 'Subsystem',
  'extract.confidence': 'Confidence:',
  'extract.conf.high': 'High',
  'extract.conf.medium': 'Medium',
  'extract.conf.low': 'Low',
  'extract.addComponentTo': '+ Add component to {name}',
  'extract.addGeneralComponent': '+ Add Component',
  'extract.includesEdits': '(Includes Edits)',
  'extract.resultsTitle': '3️⃣ Dual-Use Classification Results',
  'extract.viewRegulation': '📜 View Regulation Text & Sources (RAG)',
  'extract.similarity': 'Similarity',
  'extract.similarEntries': '🔍 Similar Control List Entries (FAISS)',
  'extract.licenseTitle': '4️⃣ License Determination',
  'extract.resultsMatch': '✅ RESULTS MATCH',
  'extract.resultsDiffer': '⚠️ RESULTS DIFFER',
  'extract.jurisdiction': '🌍 Jurisdiction Analysis (Three-Axis Model)',
  'extract.deterministicRules': 'Deterministic Rules',
  'extract.codeDriven': 'Code-Driven',
  'extract.aiAgent': 'AI Agent',
  'extract.llmDriven': 'LLM-Driven',
  'extract.licenseRequired': 'License Required:',
  'extract.authorisation': 'Authorisation:',
  'extract.legalJustification': 'Legal Justification:',
  'extract.reasoning': 'Reasoning:',
  'extract.legalReference': '📜 Legal Reference & Explanation',
  'extract.sanctionsAlert': '🚨 Sanctions Alert',
  'extract.sanctionsCheck': '✅ Sanctions Check',
  'extract.destination': 'Destination:',
  'extract.restrictions': 'Restrictions:',
  'extract.relevantMeasures': 'Restrictive measures relevant to this product:',
  'extract.allMeasures': 'Restrictive measures in force for this destination:',
  'extract.whyRestricted': 'Why This Product Is Restricted',
  'extract.sources': 'Legal basis / sources:',
  'extract.noSanctions': 'No active sanctions found for this destination.',
  'extract.riskAssessment': '🛡️ Transaction Risk Assessment',
  'extract.licenseErrorTitle': '⚠️ License Check Error',
  'extract.licenseNoteTitle': 'ℹ️ License Check Note',
  'extract.disclaimerTitle': '⚠️ AI-powered check — verify with official sources before making decisions',
  'extract.disclaimerBody': 'For informational purposes only — not legal, tax, or customs advice. AI results may be inaccurate. Always verify with a licensed professional.',
  'extract.alertEnterOne': 'Enter at least one product description!',
  'extract.alertEnterAndSelect': 'Enter a product description and select components!',
  'extract.alertSelectOne': 'Select at least one component!',
  'extract.errNoComponents': 'No components were extracted from your product descriptions. They may be too generic or not technical enough — try adding specific parameters (frequencies, materials, semiconductor tech, operating ranges) or upload a PDF with technical specs.',
  'extract.errSelectFile': 'Please select a PDF or image file first.',
  'extract.errNoProductsFile': 'No products were extracted from the uploaded file.',
  'extract.errNoProductsToCheck': 'No products to check. Add at least one product with a description.',
  'extract.noteLicenseSkipped': '⚠️ License check skipped: Please fill in Destination Country at the top of the page to check license requirements.',
  'extract.noteLicenseSkippedShort': '⚠️ License check skipped: Please fill in Destination Country to check license requirements.',

  // ── HistoryTab ────────────────────────────────────────────────────
  'history.title': 'Compliance History',
  'history.subtitle': 'Past export-control checks for your account.',
  'findings.title': 'Dual-Use Findings',
  'findings.subtitle': 'Flagged components across your checks — with control-list descriptions and destinations.',
  'findings.filterAl': 'Filter by AL number…',
  'findings.filterDestination': 'Filter by destination…',
  'findings.apply': 'Apply',
  'findings.clear': 'Clear',
  'findings.count': 'Showing {shown} of {total} findings ({positives} dual-use).',
  'findings.groupCount': '{n} components',
  'findings.view': 'View details',
  'findings.hideDetails': 'Hide details',
  'findings.download': 'Download report',
  'findings.reason': 'Dual-use reason',
  'findings.product': 'Product',
  'findings.description': 'Description',
  'findings.whyRestricted': 'Why is this restricted?',
  'findings.authorisation': 'Authorisation',
  'findings.riskScore': 'Risk score',
  'findings.empty': 'No dual-use findings match.',
  'findings.col.date': 'Date',
  'findings.col.component': 'Component',
  'findings.col.al': 'AL number',
  'findings.col.category': 'Category',
  'findings.col.destination': 'Destination',
  'findings.col.licence': 'Licence',
  'findings.col.risk': 'Risk',
  'findings.col.result': 'Result',
  'findings.dualUseOnly': 'Dual-use only',
  'findings.verdict.dualUse': 'Dual-use',
  'findings.verdict.notControlled': 'Not controlled',
  'findings.licence.required': 'Required',
  'findings.licence.notRequired': 'Not required',
  'history.filterPlaceholder': 'Filter by destination, AL number, filename, product…',
  'history.loading': 'Loading…',
  'history.refresh': 'Refresh',
  'history.loginRequired': 'Please log in to view your compliance history.',
  'history.loadFailed': 'Failed to load history (HTTP {status})',
  'history.empty': 'No compliance checks yet. Run a license check, dual-use check or PDF upload — they\'ll show up here.',
  'history.noDestination': 'no destination',
  'history.noLicence': 'No licence',
  'history.licenceRequired': 'Licence required',
  'history.noLicenceCheck': 'No licence check',
  'history.countLicence': 'licence',
  'history.countExtraction': 'extraction',
  'history.countDualUse': 'dual-use',
  'history.countUpload': 'upload',
  'history.legalFlags': 'Legal flags',
  'history.licenceChecks': 'Licence checks',
  'history.colWhen': 'When',
  'history.colAlNumbers': 'AL numbers',
  'history.colRequired': 'Required?',
  'history.colAuthorisation': 'Authorisation',
  'history.colRisk': 'Risk',
  'history.colStatus': 'Status',
  'history.componentExtractions': 'Component extractions',
  'history.colProduct': 'Product',
  'history.colComponents': 'Components',
  'history.dualUseAnalyses': 'Dual-use analyses',
  'history.clickToExpand': 'Click a row to expand per-product verdicts.',
  'history.colType': 'Type',
  'history.colResults': 'Results',
  'history.componentLevel': 'component-level',
  'history.productLevel': 'product-level',
  'history.dualUse': 'DUAL-USE',
  'history.notDualUse': 'NOT DUAL-USE',
  'history.al': 'AL:',
  'history.componentsFlagged': 'Components flagged:',
  'history.pdfUploads': 'PDF / image uploads',
  'history.colFile': 'File',
  'history.colMethod': 'Method',
  'history.colPages': 'Pages',
  'history.colProducts': 'Products',
  'history.newer': '← Newer',
  'history.older': 'Older →',
  'history.pageOf': 'Page {current} of {total}',
  'history.matches': '{count} matches',
  'history.match': '{count} match',
  // flag labels
  'flag.repair': 'Repair/reimport',
  'flag.exhibition': 'Exhibition',
  'flag.intraGroup': 'Intra-group',
  'flag.reimport120': 'Reimport ≤120d',
  'flag.freeZone': 'Customs-free zone',
  'flag.wmd': 'WMD-aware',
  'flag.authority': 'Authority-notified',
  'flag.military': 'Military end-user',
  'flag.humanRights': 'Human-rights concern',

  // ── RiskScorePanel ────────────────────────────────────────────────
  'risk.title': '🛡️ Transaction Risk Assessment',
  'risk.countryRisk': 'Country Risk',
  'risk.productSensitivity': 'Product Sensitivity',
  'risk.endUseConcern': 'End-Use Concern',
};

const de: Dict = {
  // ── Wrapper / shell ───────────────────────────────────────────────
  'wrapper.title': 'KI-Exportkontrolle',
  'wrapper.subtitle': 'Prüfen Sie Produkte vor dem Export auf EU-Dual-Use-Kontrollen, Genehmigungspflichten und Sanktionen.',
  'wrapper.tab.license': 'Lizenzprüfung',
  'wrapper.tab.extract': 'Dual-Use-Prüfung',
  'wrapper.tab.history': 'Prüfverlauf',
  'wrapper.how.title': 'So funktioniert es:',
  'wrapper.how.license': '• Lizenzprüfung: Ermittelt, ob für bestimmte AL-Nummern und ein Bestimmungsland eine Ausfuhrgenehmigung erforderlich ist.',
  'wrapper.how.dualUse': '• Dual-Use-Prüfung: Prüft ein Produkt gegen die EU-Dual-Use-Kontrolllisten.',
  'wrapper.how.extract': '– Komponenten extrahieren: Verwenden Sie dies, wenn Sie nur einen Produktnamen haben. Das System identifiziert wahrscheinliche interne Komponenten aus dem Produktnamen, erzeugt technische Beschreibungen für jede Komponente und klassifiziert sie einzeln gegen die Kontrollliste.',
  'wrapper.how.compliance': '– Compliance-Prüfung ausführen: Verwenden Sie dies, wenn Sie Beschreibungen des Fertigprodukts haben. Das Produkt wird als Ganzes gegen die Kontrollliste klassifiziert — schneller und die richtige Wahl, wenn keine Aufschlüsselung auf Komponentenebene vorliegt.',
  'wrapper.how.history': '• Prüfverlauf: Frühere Sitzungen mit ihren Eingaben, Dual-Use-Ergebnissen, Lizenzentscheidungen und Risikobewertungen einsehen.',

  // ── Shared ────────────────────────────────────────────────────────
  'common.error': 'Fehler:',
  'common.errorOccurred': 'Ein Fehler ist aufgetreten',
  'common.yes': 'JA',
  'common.no': 'NEIN',
  'common.remove': 'Entfernen',
  'common.removeProductTitle': 'Dieses Produkt entfernen',
  'common.isoHelp': '2-stelligen ISO-Ländercode eingeben (z. B. US, DE, RU, CN, GB, FR)',
  'errors.invalidIso': 'Gültigen ISO-Code eingeben',

  // ── License conditions ────────────────────────────────────────────
  'cond.legalConditions': 'Rechtliche Bedingungen',
  'cond.legalConditionsOptional': '⚖️ Rechtliche Bedingungen (Optional)',
  'cond.transactionType': 'Transaktionsart',
  'cond.repair': 'Reparatur/Ersatz',
  'cond.repairHint': 'Waren zur Wartung innerhalb von 5 Jahren wiedereingeführt (EU003)',
  'cond.exhibition': 'Vorübergehende Ausfuhr zu Ausstellung/Messe',
  'cond.exhibitionHint': 'Waren werden nach der Veranstaltung wiedereingeführt (EU004)',
  'cond.reimport120': 'Wiedereinfuhr innerhalb von 120 Tagen',
  'cond.reimport120Hint': 'Erforderlich für EU004-Berechtigung',
  'cond.intraGroup': 'Konzerninterne Verbringung',
  'cond.intraGroupHint': 'Ausfuhr an Tochter-/Schwestergesellschaft (EU007)',
  'cond.destinationConcerns': 'Bedenken zum Bestimmungsland',
  'cond.freeZone': 'Zollfreizone / Freilager',
  'cond.freeZoneHint': 'Ausfuhr in eine zollfreie Zone (sperrt alle AGG)',
  'cond.endUserConcerns': 'Bedenken zu Endverwender / Endverwendung',
  'cond.informedAuthority': 'Von zuständiger Behörde unterrichtet',
  'cond.informedAuthorityHint': 'Behörde hat über mögliche MVW-/militärische Endverwendung informiert',
  'cond.wmd': 'Kenntnis von MVW-Endverwendung',
  'cond.wmdHint': 'Ausführer weiß, dass Waren für Massenvernichtungswaffen verwendet werden könnten',
  'cond.military': 'Militärischer/sicherheitsrelevanter Endverwender',
  'cond.militaryHint': 'Empfänger ist militärische, paramilitärische, polizeiliche oder nachrichtendienstliche Stelle',
  'cond.humanRights': 'Menschenrechtsbedenken',
  'cond.humanRightsHint': 'Mögliche Verwendung zur Überwachung/Menschenrechtsverletzungen',

  // ── LicenseCheck ──────────────────────────────────────────────────
  'license.title': 'EU-Exportkontrolle – Lizenzermittlung',
  'license.countryInfo': 'Länderinformationen',
  'license.destinationLabel': 'Bestimmungsland (ISO Alpha-2):',
  'license.destinationPlaceholder': 'z. B. US, CN, IR',
  'license.originLabel': 'Ursprungsland (ISO Alpha-2):',
  'license.originPlaceholder': 'z. B. DE, US, JP',
  'license.shippingLabel': 'Versandland (ISO Alpha-2):',
  'license.shippingPlaceholder': 'z. B. DE, NL, BE',
  'license.productInfo': 'Produktinformationen',
  'license.alLabel': 'AL-Nummern (kommagetrennt):',
  'license.alPlaceholder': 'z. B. 5A002.a.1, 5D002, 3A001.a.3',
  'license.contextLabel': 'Zusätzlicher Kontext (Optional):',
  'license.contextPlaceholder': 'z. B. Kommerzielle Telekommunikationsausrüstung für den Einzelhandel...',
  'license.analyzing': 'Analysiere...',
  'license.determine': 'Lizenz ermitteln',
  'license.analysisTitle': 'Analyse der Lizenzermittlung',
  'license.determination': 'Lizenzermittlung',
  'license.codeDriven': 'Code-basiert',
  'license.licenseRequired': 'Lizenz erforderlich:',
  'license.authorisation': 'Genehmigung:',
  'license.legalJustification': 'Rechtliche Begründung:',
  'license.legalReference': '📜 Rechtsgrundlage & Erläuterung',
  'license.riskAssessment': '🛡️ Transaktionsrisikobewertung',
  'license.resultTitle': 'Ergebnis der Lizenzermittlung',
  'license.individualRequired': 'Einzelgenehmigung erforderlich:',
  'license.reason': 'Begründung:',
  'license.errBoth': 'Bitte geben Sie sowohl AL-Nummern als auch das Bestimmungsland ein',
  'license.errInvalidAl': 'Ungültiges AL-Nummernformat: {values}. Erwartet: Kategorie(0-9) + Buchstabe(A-E) + 3 Ziffern, mit optionalen punktierten Unterpunkten (z. B. 5A002, 3A001.a.3, 1C350).',
  'license.errFailed': 'Lizenzprüfung fehlgeschlagen',

  // ── ExtractComponents ─────────────────────────────────────────────
  'extract.title': 'KI-Exportkontrolle – Dual-Use-Prüfung',
  'extract.countryInfo': 'Länderinformationen',
  'extract.destinationLabel': 'Bestimmungsland (ISO):',
  'extract.originLabel': 'Ursprungsland (ISO):',
  'extract.shippingLabel': 'Versandland (ISO):',
  'extract.countryPlaceholder': 'z. B. US, CN, DE',
  'extract.exportContextLabel': 'Ausfuhrkontext (Optional):',
  'extract.exportContextPlaceholder': 'z. B. Kommerzielle Telekommunikationsausrüstung für den Einzelhandel, Reparatur und Rücksendung, Ausstellung...',
  'extract.productDescriptions': 'Produktbeschreibungen',
  'extract.product': 'Produkt',
  'extract.productPlaceholder': 'Produktbeschreibung eingeben',
  'extract.addProduct': '+ Produkt hinzufügen',
  'extract.fillExistingFirst': 'Füllen Sie zuerst die vorhandene Produktbeschreibung aus',
  'extract.clearToAddMore': 'Formular leeren, um weitere Produkte hinzuzufügen',
  'extract.extracting': 'Extrahiere...',
  'extract.extractComponents': 'Komponenten extrahieren',
  'extract.checking': 'Prüfe...',
  'extract.runCompliance': 'Compliance-Prüfung ausführen',
  'extract.clear': 'Zurücksetzen',
  'extract.orUpload': 'Oder PDF / Bild hochladen',
  'extract.uploadHint': 'Laden Sie ein technisches Dokument hoch — Komponenten werden automatisch extrahiert und unten eingefügt.',
  'extract.chooseFile': 'PDF- oder Bilddatei auswählen',
  'extract.uploading': 'Lade hoch...',
  'extract.uploadExtract': 'Hochladen & Extrahieren',
  'extract.extractedProducts': 'Extrahierte Produkte',
  'extract.extractedProductsHint': 'Wir haben diese Produkte aus Ihrer Datei extrahiert und für jedes eine Beschreibung erstellt. Überprüfen und bearbeiten Sie sie bei Bedarf — die Compliance-Prüfung verwendet die untenstehende Beschreibung unverändert.',
  'extract.productNamePlaceholder': 'Produktname',
  'extract.composedDescPlaceholder': 'Erstellte Produktbeschreibung (für Dual-Use-Prüfung verwendet)',
  'extract.runComplianceBolt': '⚡ Compliance-Prüfung ausführen',
  'extract.selectComponents': 'Komponenten für Dual-Use-Prüfung auswählen/hinzufügen',
  'extract.componentNamePlaceholder': 'Komponentenname',
  'extract.modified': 'Geändert',
  'extract.techDescPlaceholder': 'Technische Beschreibung',
  'extract.category': 'Kategorie:',
  'extract.cat.component': 'Komponente',
  'extract.cat.electronics': 'Elektronik',
  'extract.cat.sensor': 'Sensor',
  'extract.cat.subsystem': 'Subsystem',
  'extract.confidence': 'Konfidenz:',
  'extract.conf.high': 'Hoch',
  'extract.conf.medium': 'Mittel',
  'extract.conf.low': 'Niedrig',
  'extract.addComponentTo': '+ Komponente zu {name} hinzufügen',
  'extract.addGeneralComponent': '+ Allgemeine Komponente hinzufügen',
  'extract.includesEdits': '(Inkl. Änderungen)',
  'extract.resultsTitle': '3️⃣ Dual-Use-Klassifizierungsergebnisse',
  'extract.viewRegulation': '📜 Verordnungstext & Quellen anzeigen (RAG)',
  'extract.similarity': 'Ähnlichkeit',
  'extract.similarEntries': '🔍 Ähnliche Kontrolllisteneinträge (FAISS)',
  'extract.licenseTitle': '4️⃣ Lizenzermittlung',
  'extract.resultsMatch': '✅ ERGEBNISSE STIMMEN ÜBEREIN',
  'extract.resultsDiffer': '⚠️ ERGEBNISSE WEICHEN AB',
  'extract.jurisdiction': '🌍 Zuständigkeitsanalyse (Drei-Achsen-Modell)',
  'extract.deterministicRules': 'Deterministische Regeln',
  'extract.codeDriven': 'Code-basiert',
  'extract.aiAgent': 'KI-Agent',
  'extract.llmDriven': 'LLM-basiert',
  'extract.licenseRequired': 'Lizenz erforderlich:',
  'extract.authorisation': 'Genehmigung:',
  'extract.legalJustification': 'Rechtliche Begründung:',
  'extract.reasoning': 'Begründung:',
  'extract.legalReference': '📜 Rechtsgrundlage & Erläuterung',
  'extract.sanctionsAlert': '🚨 Sanktionswarnung',
  'extract.sanctionsCheck': '✅ Sanktionsprüfung',
  'extract.destination': 'Bestimmungsland:',
  'extract.restrictions': 'Beschränkungen:',
  'extract.relevantMeasures': 'Für dieses Produkt relevante restriktive Maßnahmen:',
  'extract.allMeasures': 'Für dieses Bestimmungsland geltende restriktive Maßnahmen:',
  'extract.whyRestricted': 'Warum dieses Produkt beschränkt ist',
  'extract.noSanctions': 'Keine aktiven Sanktionen für dieses Bestimmungsland gefunden.',
  'extract.riskAssessment': '🛡️ Transaktionsrisikobewertung',
  'extract.licenseErrorTitle': '⚠️ Fehler bei der Lizenzprüfung',
  'extract.licenseNoteTitle': 'ℹ️ Hinweis zur Lizenzprüfung',
  'extract.disclaimerTitle': '⚠️ KI-gestützte Prüfung — vor Entscheidungen mit offiziellen Quellen überprüfen',
  'extract.disclaimerBody': 'Nur zu Informationszwecken — keine Rechts-, Steuer- oder Zollberatung. KI-Ergebnisse können ungenau sein. Überprüfen Sie diese stets mit einer zugelassenen Fachkraft.',
  'extract.alertEnterOne': 'Geben Sie mindestens eine Produktbeschreibung ein!',
  'extract.alertEnterAndSelect': 'Geben Sie eine Produktbeschreibung ein und wählen Sie Komponenten aus!',
  'extract.alertSelectOne': 'Wählen Sie mindestens eine Komponente aus!',
  'extract.errNoComponents': 'Aus Ihren Produktbeschreibungen wurden keine Komponenten extrahiert. Sie sind möglicherweise zu allgemein oder nicht technisch genug — fügen Sie spezifische Parameter hinzu (Frequenzen, Materialien, Halbleitertechnik, Betriebsbereiche) oder laden Sie ein PDF mit technischen Daten hoch.',
  'extract.errSelectFile': 'Bitte wählen Sie zuerst eine PDF- oder Bilddatei aus.',
  'extract.errNoProductsFile': 'Aus der hochgeladenen Datei wurden keine Produkte extrahiert.',
  'extract.errNoProductsToCheck': 'Keine Produkte zu prüfen. Fügen Sie mindestens ein Produkt mit Beschreibung hinzu.',
  'extract.noteLicenseSkipped': '⚠️ Lizenzprüfung übersprungen: Bitte geben Sie oben auf der Seite das Bestimmungsland ein, um die Lizenzanforderungen zu prüfen.',
  'extract.noteLicenseSkippedShort': '⚠️ Lizenzprüfung übersprungen: Bitte geben Sie das Bestimmungsland ein, um die Lizenzanforderungen zu prüfen.',

  // ── HistoryTab ────────────────────────────────────────────────────
  'history.title': 'Compliance-Verlauf',
  'history.subtitle': 'Frühere Exportkontrollprüfungen für Ihr Konto.',
  'findings.title': 'Dual-Use-Ergebnisse',
  'findings.subtitle': 'Markierte Komponenten aus Ihren Prüfungen — mit Beschreibungen der Kontrollliste und Zielländern.',
  'findings.filterAl': 'Nach AL-Nummer filtern…',
  'findings.filterDestination': 'Nach Zielland filtern…',
  'findings.apply': 'Anwenden',
  'findings.clear': 'Zurücksetzen',
  'findings.count': '{shown} von {total} Ergebnissen ({positives} Dual-Use).',
  'findings.groupCount': '{n} Komponenten',
  'findings.view': 'Details anzeigen',
  'findings.hideDetails': 'Details ausblenden',
  'findings.download': 'Bericht herunterladen',
  'findings.reason': 'Dual-use-Begründung',
  'findings.product': 'Produkt',
  'findings.description': 'Beschreibung',
  'findings.whyRestricted': 'Warum ist dies beschränkt?',
  'findings.authorisation': 'Genehmigung',
  'findings.riskScore': 'Risikobewertung',
  'findings.empty': 'Keine passenden Dual-Use-Ergebnisse.',
  'findings.col.date': 'Datum',
  'findings.col.component': 'Komponente',
  'findings.col.al': 'AL-Nummer',
  'findings.col.category': 'Kategorie',
  'findings.col.destination': 'Zielland',
  'findings.col.licence': 'Genehmigung',
  'findings.col.risk': 'Risiko',
  'findings.col.result': 'Ergebnis',
  'findings.dualUseOnly': 'Nur Dual-Use',
  'findings.verdict.dualUse': 'Dual-Use',
  'findings.verdict.notControlled': 'Nicht kontrolliert',
  'findings.licence.required': 'Erforderlich',
  'findings.licence.notRequired': 'Nicht erforderlich',
  'history.filterPlaceholder': 'Nach Bestimmungsland, AL-Nummer, Dateiname, Produkt filtern…',
  'history.loading': 'Lade…',
  'history.refresh': 'Aktualisieren',
  'history.loginRequired': 'Bitte melden Sie sich an, um Ihren Compliance-Verlauf anzuzeigen.',
  'history.loadFailed': 'Verlauf konnte nicht geladen werden (HTTP {status})',
  'history.empty': 'Noch keine Compliance-Prüfungen. Führen Sie eine Lizenzprüfung, Dual-Use-Prüfung oder einen PDF-Upload durch — sie erscheinen hier.',
  'history.noDestination': 'kein Bestimmungsland',
  'history.noLicence': 'Keine Lizenz',
  'history.licenceRequired': 'Lizenz erforderlich',
  'history.noLicenceCheck': 'Keine Lizenzprüfung',
  'history.countLicence': 'Lizenz',
  'history.countExtraction': 'Extraktion',
  'history.countDualUse': 'Dual-Use',
  'history.countUpload': 'Upload',
  'history.legalFlags': 'Rechtliche Kennzeichen',
  'history.licenceChecks': 'Lizenzprüfungen',
  'history.colWhen': 'Wann',
  'history.colAlNumbers': 'AL-Nummern',
  'history.colRequired': 'Erforderlich?',
  'history.colAuthorisation': 'Genehmigung',
  'history.colRisk': 'Risiko',
  'history.colStatus': 'Status',
  'history.componentExtractions': 'Komponentenextraktionen',
  'history.colProduct': 'Produkt',
  'history.colComponents': 'Komponenten',
  'history.dualUseAnalyses': 'Dual-Use-Analysen',
  'history.clickToExpand': 'Klicken Sie auf eine Zeile, um produktbezogene Bewertungen einzublenden.',
  'history.colType': 'Typ',
  'history.colResults': 'Ergebnisse',
  'history.componentLevel': 'Komponentenebene',
  'history.productLevel': 'Produktebene',
  'history.dualUse': 'DUAL-USE',
  'history.notDualUse': 'KEIN DUAL-USE',
  'history.al': 'AL:',
  'history.componentsFlagged': 'Markierte Komponenten:',
  'history.pdfUploads': 'PDF-/Bild-Uploads',
  'history.colFile': 'Datei',
  'history.colMethod': 'Methode',
  'history.colPages': 'Seiten',
  'history.colProducts': 'Produkte',
  'history.newer': '← Neuer',
  'history.older': 'Älter →',
  'history.pageOf': 'Seite {current} von {total}',
  'history.matches': '{count} Treffer',
  'history.match': '{count} Treffer',
  // flag labels
  'flag.repair': 'Reparatur/Wiedereinfuhr',
  'flag.exhibition': 'Ausstellung',
  'flag.intraGroup': 'Konzernintern',
  'flag.reimport120': 'Wiedereinfuhr ≤120 T',
  'flag.freeZone': 'Zollfreizone',
  'flag.wmd': 'MVW-Kenntnis',
  'flag.authority': 'Behördlich unterrichtet',
  'flag.military': 'Militärischer Endverwender',
  'flag.humanRights': 'Menschenrechtsbedenken',

  // ── RiskScorePanel ────────────────────────────────────────────────
  'risk.title': '🛡️ Transaktionsrisikobewertung',
  'risk.countryRisk': 'Länderrisiko',
  'risk.productSensitivity': 'Produktsensibilität',
  'risk.endUseConcern': 'Endverwendungsbedenken',
};

const resources: Record<Lang, Dict> = { en, de };

export type ECTranslate = (key: string, vars?: Record<string, string | number>) => string;

function format(str: string, vars?: Record<string, string | number>): string {
  if (!vars) return str;
  let out = str;
  for (const [k, v] of Object.entries(vars)) {
    out = out.split(`{${k}}`).join(String(v));
  }
  return out;
}

/**
 * Backend-generated result text (license `reason`, sanctions `warnings`) is
 * assembled in Python — see backend `EC/ai_license/agent_runner.py` and
 * `rules.py` — and the API never receives the UI language, so it always comes
 * back in English. Until the backend is language-aware, we localize the known
 * fixed phrases here on the frontend. Phrases are matched in order; the dynamic
 * country code / sanction level is preserved via capture groups. Unknown text
 * (e.g. RAG-retrieved legal citations) falls through unchanged.
 */
// Each entry is [match, replacement]. RegExp entries carry capture groups for
// dynamic bits (country code, sanction level); plain strings are matched
// literally (no regex-escaping needed). Applied in order, so list any phrase
// that is a substring of another phrase first.
const deResultPhrases: Array<[RegExp | string, string]> = [
  [/Export to (.+?) is prohibited or severely restricted due to embargo\./g,
    'Die Ausfuhr nach $1 ist aufgrund eines Embargos verboten oder stark eingeschränkt.'],
  [/However, export to (.+?) is prohibited due to (\S+) sanctions\./g,
    'Die Ausfuhr nach $1 ist jedoch aufgrund von $2-Sanktionen verboten.'],
  ['SANCTIONS OVERRIDE:', 'SANKTIONSÜBERSTEUERUNG:'],
  ['SANCTIONS ALERT:', 'SANKTIONSWARNUNG:'],
  ['🚫 EMBARGO: Significant export restrictions apply', '🚫 EMBARGO: Es gelten erhebliche Ausfuhrbeschränkungen'],
  ['• Arms embargo in effect', '• Waffenembargo in Kraft'],
  ['• Dual-use items restricted', '• Dual-Use-Güter eingeschränkt'],
  // Risk recommendation (EC/risk_scoring.py :: _recommendation) — band line + drivers.
  ['🚨 Manual review strongly recommended.', '🚨 Manuelle Prüfung dringend empfohlen.'],
  ['⚠️ Manual review recommended.', '⚠️ Manuelle Prüfung empfohlen.'],
  ['ℹ️ Standard compliance procedures apply.', 'ℹ️ Es gelten die üblichen Compliance-Verfahren.'],
  ['✅ Low-risk transaction — standard processing.', '✅ Transaktion mit geringem Risiko — Standardbearbeitung.'],
  ['Key drivers:', 'Hauptfaktoren:'],
  ['high country risk (embargo/partial embargo)', 'hohes Länderrisiko (Embargo/Teilembargo)'],
  ['elevated country risk (watchlist)', 'erhöhtes Länderrisiko (Beobachtungsliste)'],
  ['highly sensitive product category', 'hochsensible Produktkategorie'],
  ['controlled product', 'kontrolliertes Produkt'],
  ['serious end-use/end-user concerns', 'schwerwiegende Bedenken zur Endverwendung/zum Endverwender'],
  ['end-use flags present', 'Endverwendungs-Kennzeichen vorhanden'],
];

// Sanctions "restrictions" tags are a fixed controlled vocabulary.
const deRestrictions: Record<string, string> = {
  arms: 'Waffen',
  'dual-use': 'Dual-Use',
  technology: 'Technologie',
};

/** Localize a free-text backend result string (reason / warning line). */
export function localizeResultText(text: string | null | undefined, lang: Lang): string {
  if (!text || lang !== 'de') return text ?? '';
  let out = text;
  for (const [pattern, replacement] of deResultPhrases) {
    out = typeof pattern === 'string'
      ? out.split(pattern).join(replacement)
      : out.replace(pattern, replacement);
  }
  return out;
}

/** Localize a single sanctions restriction tag (falls back to the raw tag). */
export function localizeRestriction(tag: string, lang: Lang): string {
  if (lang !== 'de') return tag;
  return deRestrictions[tag] ?? tag;
}

/**
 * Hook used by every export-control component. Reuses the app's `useTranslation`
 * (which subscribes to the language store) for the current language, then
 * resolves keys against the feature-local dictionary above.
 */
export function useECTranslation(): { t: ECTranslate; lang: Lang } {
  const { currentLanguage } = useAppTranslation();
  const lang: Lang = currentLanguage === 'de' ? 'de' : 'en';
  const t: ECTranslate = (key, vars) => format(resources[lang][key] ?? resources.en[key] ?? key, vars);
  return { t, lang };
}
